import { Controller } from "@hotwired/stimulus"

// The announcement form's live side column: counts who the audience reaches
// (via the reach endpoint) and previews the push as it would land.
//
// Counts refresh on any change inside the form and when affinity chips are
// added or removed (the picker adds hidden inputs, which fire no event).
export default class extends Controller {
  static targets = [
    "form", "audience", "title", "body",
    "reach", "reachable", "matching", "reachNote", "reachErrors", "compactReach",
    "previewTitle", "previewBody", "previewDestination"
  ]
  static values = { reachUrl: String }

  connect() {
    this.requestId = 0
    this.lastQuery = null
    this.onChange = () => { this.preview(); this.scheduleReach() }
    this.formTarget.addEventListener("change", this.onChange)
    this.formTarget.addEventListener("input", this.onChange)
    this.observer = new MutationObserver(() => this.scheduleReach())
    this.observer.observe(this.audienceTarget, { childList: true, subtree: true })
    this.preview()
    this.refreshReach()
  }

  disconnect() {
    this.formTarget.removeEventListener("change", this.onChange)
    this.formTarget.removeEventListener("input", this.onChange)
    this.observer?.disconnect()
    clearTimeout(this.timer)
    this.controller?.abort()
  }

  preview() {
    this.fill(this.previewTitleTarget, this.titleTarget.value.trim(), "Title")
    this.fill(this.previewBodyTarget, this.bodyTarget.value.trim(), "Message")
    this.previewDestinationTarget.textContent = this.destination()
  }

  fill(element, text, placeholder) {
    element.textContent = text || placeholder
    element.classList.toggle("is-placeholder", !text)
  }

  // The picked option's name, with its group for records ("Catalog: …"), or
  // the typed path for a custom one.
  destination() {
    const select = this.formTarget.querySelector("#announcement_deep_link")
    if (!select) return "Home"
    if (select.value === "custom") {
      const path = this.formTarget.querySelector("#announcement_custom_deep_link")?.value.trim()
      return path || "No path yet. Type one under Opens in app."
    }
    const option = select.selectedOptions[0]
    if (!option) return "Home"
    const group = option.parentElement.tagName === "OPTGROUP" ? option.parentElement.label : null
    // Groups are plural ("Catalogs"); the show page labels one "Catalog: …".
    return group && group !== "Screens" ? `${group.replace(/s$/, "")}: ${option.text}` : option.text
  }

  scheduleReach() {
    clearTimeout(this.timer)
    this.timer = setTimeout(() => this.refreshReach(), 300)
  }

  async refreshReach() {
    const query = this.audienceQuery()
    if (query === this.lastQuery) return
    this.lastQuery = query

    this.controller?.abort()
    this.controller = new AbortController()
    const id = ++this.requestId
    this.showCounting()

    try {
      const response = await fetch(`${this.reachUrlValue}?${query}`, {
        headers: { Accept: "application/json" },
        signal: this.controller.signal
      })
      if (!response.ok) throw new Error(`HTTP ${response.status}`)
      const data = await response.json()
      if (id === this.requestId) this.showReach(data)
    } catch (error) {
      if (error.name === "AbortError" || id !== this.requestId) return
      console.error("[announcement-compose] reach failed:", error)
      this.lastQuery = null
      this.showFailure()
    }
  }

  audienceQuery() {
    const params = new URLSearchParams()
    for (const [name, value] of new FormData(this.formTarget)) {
      if (name.startsWith("announcement[audience]") && value !== "") params.append(name, value)
    }
    return params.toString()
  }

  showCounting() {
    this.reachTarget.setAttribute("aria-busy", "true")
    this.reachableTarget.classList.add("opacity-50")
    this.matchingTarget.classList.add("opacity-50")
    this.reachNoteTarget.textContent = "Counting…"
  }

  showReach(data) {
    this.reachTarget.setAttribute("aria-busy", "false")
    this.reachableTarget.classList.remove("opacity-50")
    this.matchingTarget.classList.remove("opacity-50")

    const errors = data.errors || []
    this.reachErrorsTarget.replaceChildren(...errors.map(message => {
      const item = document.createElement("li")
      item.textContent = message
      return item
    }))
    this.reachErrorsTarget.classList.toggle("d-none", errors.length === 0)

    if (errors.length) {
      this.reachableTarget.textContent = "–"
      this.matchingTarget.textContent = "–"
      this.reachNoteTarget.textContent = "Fix the audience to see who it reaches."
      this.compactReachTarget.textContent = "Fix the audience to see who it reaches."
      return
    }

    const reachable = data.reachable.toLocaleString()
    const matching = data.matching.toLocaleString()
    this.reachableTarget.textContent = reachable
    this.matchingTarget.textContent = matching
    let note
    if (data.matching === 0) note = "No members match. Loosen a filter."
    else if (data.reachable === 0) note = "None of the matching members have the app."
    else if (data.everyone) note = "No filters, so this goes to every member with the app."
    else if (data.matching === data.reachable) note = "Every matching member has the app."
    else note = `${this.pluralize(data.matching - data.reachable, "matching member")} without the app won't get it.`
    this.reachNoteTarget.textContent = note
    this.compactReachTarget.textContent = `Reach: ${reachable} get the push (${matching} match).`
  }

  showFailure() {
    this.reachTarget.setAttribute("aria-busy", "false")
    this.reachableTarget.classList.remove("opacity-50")
    this.matchingTarget.classList.remove("opacity-50")
    this.reachNoteTarget.textContent = "Couldn't count the audience. Change a filter to try again."
    this.compactReachTarget.textContent = "Couldn't count the audience."
  }

  pluralize(count, word) {
    return `${count.toLocaleString()} ${count === 1 ? word : `${word}s`}`
  }
}
